// @ts-nocheck
/**
 * SmsScreen — Messagerie (W4, B2-sms).
 *
 * SMS: conversations on the left, the open thread (or a new message) on the
 * right. Email: shown only once the agent's own mailbox (Gmail / Outlook) is
 * connected — before, the tab only led to « aucun e-mail envoyé » while
 * Intégrations said both were unavailable. `?tab=email` without a mailbox
 * falls back to SMS. A failed email read says so (it looked like an empty
 * history).
 *
 * Doc: docs/refonte-ui/audit-ui-ux.md « /ParallelDialer/sms ».
 */
import { Button } from '@/components/ui/button';
import React, { useState } from 'react';
import SmsConversations from '@/components/dialer/SmsConversations';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageSquare, Mail, Inbox, AlertCircle } from 'lucide-react';
import { useDialer } from '@/contexts/DialerContext';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import SMSPanel from '@/components/dialer/SMSPanel';
import SmsThread from '@/components/dialer/SmsThread';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate } from '@/i18n/locale';
import { useTabParam } from '@/hooks/useTabParam';
// ─── Email history section ────────────────────────────────────────────────────

function EmailHistorySection({ clientEmail }) {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { data: emails = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['email-history', clientEmail],
    queryFn: async () => {
      const result = await api.functions.invoke('listEmails', { clientEmail });
      const raw = result?.data;
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.emails)) return raw.emails;
      return [];
    },
    retry: false,
    enabled: !!clientEmail,
  });

  if (isLoading) {
    return (
      <div className="bg-card border border-border/50 rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('pages:sms.emailsSent')}</h3>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse h-16 bg-muted rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-card border border-border/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('pages:sms.emailsSent')}</h3>
        <div role="alert" className="text-center py-10">
          <AlertCircle className="w-8 h-8 text-[var(--vf-status-failed-fg)] mx-auto mb-3" aria-hidden />
          <p className="text-sm text-foreground">{t('pages:sms.emailsLoadError')}</p>
          <Button size="sm" variant="outline" className="mt-4 min-h-[44px]" onClick={() => refetch()}>
            {t('common:actions.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="bg-card border border-border/50 rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('pages:sms.emailsSent')}</h3>
        {/* Kevin, 2026-09-30 : juste un bouton sous la phrase. Le bandeau
            d'alerte jaune qui disait la meme chose plus haut est parti. */}
        <div className="text-center py-10">
          <Inbox className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{t('pages:sms.noEmailsSent')}</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-4 h-8 text-xs"
            onClick={() => navigate('/ParallelDialer/settings?section=integrations')}
          >
            {t('pages:smsPanel.empty.noMailbox.cta')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border/50 rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">{t('pages:sms.emailsSent')}</h3>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {emails.map((email, idx) => (
          <div
            key={email.id || idx}
            className="surface-1 rounded-lg p-4 border border-border flex items-start gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm font-medium text-foreground truncate">
                  {email.subject || t('pages:sms.noSubject')}
                </p>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {email.sent_at || email.created_date
                    ? formatDate(email.sent_at || email.created_date, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                    : '—'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {t('pages:sms.toRecipient', { to: email.to || email.recipient || '—' })}
              </p>
            </div>
            {email.status && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex-shrink-0 ${
                  email.status === 'delivered' || email.status === 'sent'
                    ? 'bg-green-100 text-green-700'
                    : email.status === 'failed'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}
              >
                {t(`pages:sms.emailStatus.${email.status}`, { defaultValue: email.status })}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

// Tabs SMS/Email — pattern border-bottom sobre (aligne sur AnalyticsScreen),
// plus de barre pleine largeur qui noyait la hierarchie.
const MESSAGING_TABS = [
  { id: 'sms',   labelKey: 'pages:sms.tabs.sms',   icon: MessageSquare },
  { id: 'email', labelKey: 'pages:sms.tabs.email', icon: Mail },
];

export default function SmsScreen() {
  const { t } = useTranslation(['pages']);
  const { callerData } = useDialer();
  // The agent's own mailbox (same query and cache as SMSPanel). The Email
  // tab exists only once one is connected (W4, B2-sms).
  const { data: emailCapabilities } = useQuery({
    queryKey: ['email-capabilities'],
    queryFn: async () => {
      const r = await api.functions.invoke('emailCapabilities');
      return r?.data ?? r;
    },
    staleTime: 60_000,
  });
  const mailboxConnected = !!emailCapabilities?.callerProvider;
  const tabs = mailboxConnected ? MESSAGING_TABS : MESSAGING_TABS.filter((x) => x.id !== 'email');
  // L'onglet vit dans l'URL (?tab=) : il se partage, se met en favori, et
  // revenir en arrière rouvre l'onglet qu'on quittait.
  const [activeTab, setActiveTab] = useTabParam(tabs.map((x) => x.id), 'sms');
  const [conversation, setConversation] = useState(null);

  return (
    <motion.div
      key="messaging"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-5"
    >
      {/* Tabs SMS/Email — composant DS variante "underline" (même que
          la nav Settings — cohérence visuelle garantie). */}
      {tabs.length > 1 && (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList variant="underline">
          {tabs.map(({ id, labelKey, icon: Icon }) => (
            <TabsTrigger key={id} value={id} variant="underline">
              <Icon className="w-3.5 h-3.5" />
              {t(labelKey)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      )}

      {activeTab === 'sms' && (
        /* Les conversations a gauche, la redaction a droite. Elles etaient
           empilees : il fallait faire defiler toute la liste pour atteindre
           le champ de saisie, et on perdait de vue a qui on ecrivait. Sur
           telephone elles restent empilees, la largeur ne permet pas mieux. */
        <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr] gap-5 items-start">
          <SmsConversations
            selectedPhone={conversation?.phone}
            onSelect={(phone, _prospectId, name) => setConversation({ phone, name })}
            onCompose={() => setConversation(null)}
          />
          {/* An open conversation takes the middle: its messages, newest at
              the bottom, and the reply field. Without one, the form to
              write to someone new (Kevin, 2026-10-07). The full history of
              every number is not shown: the list on the left is that. */}
          {conversation ? (
            <SmsThread phone={conversation.phone} name={conversation.name} />
          ) : (
            <SMSPanel
              clientEmail={callerData?.client_email}
              lockedMode="sms"
              showHistory={false}
            />
          )}
        </div>
      )}

      {activeTab === 'email' && (
        <div className="space-y-6">
          <SMSPanel
            clientEmail={callerData?.client_email}
            lockedMode="email"
          />
          <EmailHistorySection clientEmail={callerData?.client_email} />
        </div>
      )}

    </motion.div>
  );
}
